/**
 * SWM Analytics — KPIs & Analytics tab for Structural Workfront.
 *
 * Four sections (per the user's KPI spec):
 *   1. Monthly Structural Steel Production Control — 6 KPI tiles with an
 *      editable target per month.
 *   2. Fabrication Flow — 7-stage funnel + KPI trio
 *      (Achievement % · FTR % · Rejection %).
 *   3. Erection Flow — 7-stage funnel + KPI trio
 *      (Achievement % · Readiness % · Balance to Erect).
 *   4. Monthly Trend — Target vs Actual vs Forecast bar+line chart per
 *      metric (Production / Fabrication / Erection).
 *
 * Legacy blocks (allocation donut, weight comparison, fab/erection
 * waterfalls) are preserved below the new sections for continuity.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { useTheme } from '@/contexts/ThemeContext';
import { Loader2, Zap, Target, Activity, Gauge, TrendingUp, Package, AlertTriangle, CheckCircle, Save, ChevronLeft, ChevronRight, Sparkles, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ComposedChart, Line, LabelList,
} from 'recharts';
import { KpiBuilderChat } from './KpiBuilderChat';
import { WidgetChart } from './WidgetChart';

const fmtKg = (v) => `${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} kg`;
const fmtPct = (v) => `${Number(v || 0).toFixed(1)}%`;
const monthNow = () => new Date().toISOString().slice(0, 7);
const shiftMonth = (m, delta) => {
  const [y, mo] = m.split('-').map(Number);
  const d = new Date(Date.UTC(y, mo - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

function KPICard({ label, value, sub, icon: Icon, color, testId }) {
  return (
    <div className="p-3 rounded-lg border transition-all hover:scale-[1.02]"
         style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}
         data-testid={testId || `swm-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-[9px] font-medium uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>{label}</p>
          <p className="text-xl font-bold mt-0.5 truncate" style={{ color: color || 'var(--text-primary)' }}>{value}</p>
          {sub && <p className="text-[10px] mt-0.5 truncate" style={{ color: 'var(--text-muted)' }}>{sub}</p>}
        </div>
        {Icon && <Icon className="w-4 h-4 shrink-0" style={{ color: color || '#F59206', opacity: 0.6 }} />}
      </div>
    </div>
  );
}

function ProductionControl({ projectId, month, onMonthChange }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [tgt, setTgt] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/v1/projects/${projectId}/swm/kpi/production-control`, { params: { month } });
      setData(res.data);
      setTgt(res.data?.monthlyTarget || '');
    } catch (e) { console.warn(e); }
  }, [projectId, month]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/v1/projects/${projectId}/swm/kpi/monthly-targets/${month}`, {
        productionTarget: Number(tgt) || 0,
      });
      toast.success(`Production target saved for ${month}`);
      setEditing(false);
      await load();
    } catch (e) { toast.error('Failed to save target'); }
    setSaving(false);
  };

  const cardColor = (v) => (v == null ? 'var(--text-primary)' : v >= 100 ? '#22c55e' : v >= 75 ? '#f59e0b' : '#ef4444');
  const achColor = data ? cardColor(data.achievementPct) : 'var(--text-primary)';

  return (
    <div className="p-4 rounded-lg border" data-testid="swm-production-control"
         style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4" style={{ color: '#F59206' }} />
          <h4 className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
            Monthly Structural Steel Production Control
          </h4>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => onMonthChange(shiftMonth(month, -1))} data-testid="swm-month-prev">
            <ChevronLeft className="w-3.5 h-3.5" />
          </Button>
          <div className="text-xs font-mono font-semibold px-2 py-1 rounded"
               style={{ background: 'var(--card-bg-alt)', color: 'var(--text-primary)' }}
               data-testid="swm-month-label">{month}</div>
          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => onMonthChange(shiftMonth(month, 1))} data-testid="swm-month-next">
            <ChevronRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        <div className="p-3 rounded-lg border" style={{ background: 'var(--card-bg-alt)', borderColor: 'var(--border-color)' }} data-testid="swm-kpi-monthly-target">
          <p className="text-[9px] font-medium uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Monthly Target</p>
          {editing ? (
            <div className="flex items-center gap-1 mt-1">
              <Input value={tgt} onChange={(e) => setTgt(e.target.value)} className="h-7 text-sm" type="number" step="1" data-testid="swm-target-input" />
              <Button size="sm" className="h-7 px-2" onClick={save} disabled={saving} data-testid="swm-target-save">
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              </Button>
            </div>
          ) : (
            <div className="flex items-baseline justify-between">
              <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{fmtKg(data?.monthlyTarget)}</p>
              <button onClick={() => setEditing(true)} className="text-[9px] underline" style={{ color: 'var(--accent-primary)' }} data-testid="swm-target-edit">Edit</button>
            </div>
          )}
          <p className="text-[9px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
            {data?.targetSource === 'USER_INPUT' ? 'User set' : 'Schedule-derived'}
          </p>
        </div>
        <KPICard label="Actual Achievement" value={fmtKg(data?.actualAchievement)} icon={Activity} color="#3b82f6" testId="swm-kpi-actual" />
        <KPICard label="Achievement %" value={fmtPct(data?.achievementPct)} icon={TrendingUp} color={achColor} testId="swm-kpi-achievement-pct" />
        <KPICard label="Gap to Target" value={fmtKg(data?.gapToTarget)} sub={data?.gapToTarget > 0 ? 'behind target' : 'on/above target'} icon={AlertTriangle} color={data?.gapToTarget > 0 ? '#ef4444' : '#22c55e'} testId="swm-kpi-gap" />
        <KPICard label="Forecast (Month End)" value={fmtKg(data?.forecast)} sub={data?.scheduleForecast > 0 ? 'schedule-driven' : 'run-rate'} icon={Gauge} color="#8b5cf6" testId="swm-kpi-forecast" />
        <KPICard label="Material Readiness" value={fmtPct(data?.materialReadinessPct)} sub="100% allocated" icon={CheckCircle} color={cardColor(data?.materialReadinessPct)} testId="swm-kpi-material-readiness" />
      </div>
    </div>
  );
}

function FlowFunnel({ title, data, kpis, testId, gridColor, tickColor }) {
  const rows = data?.flow || [];
  const maxWt = Math.max(1, ...rows.map((r) => r.weight));
  return (
    <div className="p-4 rounded-lg border" data-testid={testId}
         style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{title}</h4>
        <div className="flex items-center gap-2 flex-wrap">
          {kpis.map((k) => (
            <div key={k.label} className="text-[10px] px-2 py-1 rounded"
                 style={{ background: `${k.color}20`, color: k.color, border: `1px solid ${k.color}40` }}
                 data-testid={`${testId}-kpi-${k.label.replace(/\s+/g, '-').toLowerCase()}`}>
              <span className="font-semibold">{k.label}:</span> <span className="font-mono ml-1">{k.value}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-1.5">
        {rows.map((r, i) => {
          const pct = Math.max(2, Math.round(100 * (r.weight / maxWt)));
          return (
            <div key={i} className="flex items-center gap-2" data-testid={`${testId}-row-${i}`}>
              <div className="w-40 shrink-0 text-[11px] font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                {r.stage}
              </div>
              <div className="flex-1 relative h-6 rounded" style={{ background: 'var(--card-bg-alt)' }}>
                <div className="h-full rounded transition-all"
                     style={{ width: `${pct}%`, background: r.color, opacity: 0.9 }} />
                <div className="absolute inset-0 flex items-center px-2 text-[10px] font-semibold"
                     style={{ color: pct > 20 ? '#fff' : 'var(--text-primary)' }}>
                  {fmtKg(r.weight)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TrendChart({ projectId, gridColor, tickColor }) {
  const [trend, setTrend] = useState([]);
  const [metric, setMetric] = useState('fabrication'); // fabrication | erection

  useEffect(() => {
    api.get(`/v1/projects/${projectId}/swm/kpi/trend`, { params: { months: 6 } })
      .then((r) => setTrend(r.data?.trend || []))
      .catch(() => {});
  }, [projectId]);

  const chartData = trend.map((t) => ({
    month: t.month,
    Target:   t[`${metric}Target`],
    Actual:   t[`${metric}Actual`],
    Forecast: t[`${metric}Forecast`],
    isCurrent: t.isCurrent,
  }));

  const METRICS = [
    { key: 'fabrication',  label: 'Fabrication' },
    { key: 'erection',     label: 'Erection' },
  ];

  return (
    <div className="p-4 rounded-lg border" data-testid="swm-trend-chart"
         style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h4 className="text-sm font-bold min-w-0" style={{ color: 'var(--text-primary)' }}>
          Monthly Target vs. Actual vs. Forecast · Trend
        </h4>
        <div className="flex items-center gap-1 shrink-0 rounded-md p-0.5"
             style={{ background: 'var(--card-bg-alt, rgba(148,163,184,0.12))', border: '1px solid var(--border-color)' }}
             data-testid="swm-trend-tabs">
          {METRICS.map((m) => {
            const active = metric === m.key;
            return (
              <button
                key={m.key}
                onClick={() => setMetric(m.key)}
                className="text-[11px] px-3 py-1 rounded font-semibold transition-all whitespace-nowrap"
                style={{
                  background: active ? '#F26B1F' : 'transparent',
                  color:      active ? '#FFFFFF' : 'var(--text-primary)',
                  boxShadow:  active ? '0 1px 2px rgba(242,107,31,0.35)' : 'none',
                }}
                data-testid={`swm-trend-tab-${m.key}`}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="h-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="month" tick={{ fill: tickColor, fontSize: 10 }} />
            <YAxis tick={{ fill: tickColor, fontSize: 10 }} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v)} />
            <Tooltip
              contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', fontSize: 11 }}
              formatter={(v) => fmtKg(v)}
            />
            <Legend wrapperStyle={{ fontSize: 10, color: tickColor }} />
            <Bar dataKey="Target"   fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Actual"   fill="#22c55e" radius={[4, 4, 0, 0]} />
            <Line dataKey="Forecast" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4, fill: '#f59e0b' }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function SWMAnalytics({ projectId }) {
  const { isDark } = useTheme();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(monthNow());
  const [fabFlow, setFabFlow] = useState(null);
  const [erectFlow, setErectFlow] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [customWidgets, setCustomWidgets] = useState([]);
  const [customLoading, setCustomLoading] = useState(false);

  const gridColor = isDark ? '#1e293b' : '#e2e8f0';
  const tickColor = isDark ? '#64748b' : '#94a3b8';

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, fRes, eRes] = await Promise.all([
        api.get(`/v1/projects/${projectId}/swm/summary`).catch(() => null),
        api.get(`/v1/projects/${projectId}/swm/kpi/fabrication-flow`, { params: { month } }).catch(() => null),
        api.get(`/v1/projects/${projectId}/swm/kpi/erection-flow`, { params: { month } }).catch(() => null),
      ]);
      if (sRes) setSummary(sRes.data);
      if (fRes) setFabFlow(fRes.data);
      if (eRes) setErectFlow(eRes.data);
    } finally { setLoading(false); }
  }, [projectId, month]);

  const fetchCustomWidgets = useCallback(async () => {
    setCustomLoading(true);
    try {
      const res = await api.get(`/v1/projects/${projectId}/swm/kpi-builder/widgets`);
      setCustomWidgets(res.data?.widgets || []);
    } catch { /* silent — feature is additive */ }
    finally { setCustomLoading(false); }
  }, [projectId]);

  const deleteWidget = async (widget_id) => {
    try {
      await api.delete(`/v1/projects/${projectId}/swm/kpi-builder/widgets/${widget_id}`);
      setCustomWidgets((prev) => prev.filter((w) => w.widget_id !== widget_id));
      toast.success('Widget removed');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove'); }
  };

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { fetchCustomWidgets(); }, [fetchCustomWidgets]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;

  const counts = summary?.counts || {};
  const dist = summary?.stateDistribution || {};
  const weights = summary?.weights || {};
  const fabProgress = summary?.fabricationProgress || {};
  const erecProgress = summary?.erectionProgress || {};

  const stateData = [
    { name: 'Unallocated',      value: dist.unallocated || 0,     fill: '#ef4444' },
    { name: 'Partial Soft',     value: dist.partialSoft || 0,     fill: '#eab308' },
    { name: 'Ready Soft',       value: dist.readySoft || 0,       fill: '#3b82f6' },
    { name: 'PO Ready',         value: dist.poReady || 0,         fill: '#143A6B' },
    { name: 'Partial Hard',     value: dist.partialHard || 0,     fill: '#f59e0b' },
    { name: 'Fully Allocated',  value: dist.fullyAllocated || 0,  fill: '#22c55e' },
  ].filter((d) => d.value > 0);

  const weightData = [
    { name: 'MTO Weight', value: weights.totalMTOWeight || 0, fill: '#3b82f6' },
    { name: 'Ordered',    value: weights.totalOrderedWeight || 0, fill: '#143A6B' },
    { name: 'Stock',      value: weights.totalStockWeight || 0, fill: '#f59e0b' },
    { name: 'Mark Wt',    value: weights.totalMarkWeight || 0, fill: '#22c55e' },
  ];

  const fabKpis = fabFlow ? [
    { label: 'Achievement %', value: fmtPct(fabFlow.achievementPct), color: (fabFlow.achievementPct || 0) >= 75 ? '#22c55e' : '#ef4444' },
    { label: 'FTR %',         value: fmtPct(fabFlow.ftrPct),         color: '#3b82f6' },
    { label: 'Rejection %',   value: fmtPct(fabFlow.rejectionPct),   color: '#ef4444' },
    { label: 'Ready for Erection', value: fmtKg(fabFlow.readyForErectionWeight), color: '#10b981' },
  ] : [];

  const erectKpis = erectFlow ? [
    { label: 'Achievement %', value: fmtPct(erectFlow.achievementPct), color: (erectFlow.achievementPct || 0) >= 75 ? '#22c55e' : '#ef4444' },
    { label: 'Readiness %',   value: fmtPct(erectFlow.readinessPct),   color: '#3b82f6' },
    { label: 'WIP',           value: fmtKg(erectFlow.wip),             color: '#f59e0b' },
    { label: 'Balance to Erect', value: fmtKg(erectFlow.balanceToErect), color: '#64748b' },
  ] : [];

  return (
    <div className="space-y-4" data-testid="swm-analytics">
      {/* Top action row — "Build with AI" launches the Cognara chat drawer */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>KPIs & Analytics</h3>
          <p className="text-[11px] opacity-60">Standard KPIs below · build your own with Cognara AI</p>
        </div>
        <button onClick={() => setBuilderOpen(true)} data-testid="swm-open-kpi-builder"
          className="group flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-semibold transition-all hover:shadow-lg"
          style={{ background: 'linear-gradient(135deg, #F26B1F, #8b5cf6)', color: '#fff' }}>
          <Sparkles className="w-3.5 h-3.5 transition-transform group-hover:rotate-12" />
          Build a KPI with AI
        </button>
      </div>

      {/* Top KPI strip — preserved from legacy view */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        <KPICard label="Structures" value={counts.structures || 0} icon={Package} color="#3b82f6" />
        <KPICard label="Tags" value={counts.tags || 0} sub={`${dist.fullyAllocated || 0} fully allocated`} icon={Target} color="#F59206" />
        <KPICard label="Mark Numbers" value={counts.markNumbers || 0} icon={Zap} color="#22c55e" />
        <KPICard label="Fab Progress" value={fmtPct(summary?.fabricationProgressPct)} sub={`${fmtKg(fabProgress.dispatch)} dispatched`} icon={Activity} color="#143A6B" />
        <KPICard label="Erection Progress" value={fmtPct(summary?.erectionProgressPct)} sub={`${fmtKg(erecProgress.grouting)} grouted`} icon={TrendingUp} color="#f59e0b" />
        <KPICard label="MTO Weight" value={fmtKg(weights.totalMTOWeight)} sub={`Ordered: ${fmtKg(weights.totalOrderedWeight)}`} icon={Gauge} color="#3b82f6" />
      </div>

      {/* 1. Monthly Production Control */}
      <ProductionControl projectId={projectId} month={month} onMonthChange={setMonth} />

      {/* 2. Fabrication Flow Funnel */}
      <FlowFunnel
        title="Fabrication — Material Flow & Target Tracking"
        data={fabFlow}
        kpis={fabKpis}
        testId="swm-fabrication-flow"
        gridColor={gridColor}
        tickColor={tickColor}
      />

      {/* 3. Erection Flow Funnel */}
      <FlowFunnel
        title="Erection — Material Availability & Progress"
        data={erectFlow}
        kpis={erectKpis}
        testId="swm-erection-flow"
        gridColor={gridColor}
        tickColor={tickColor}
      />

      {/* 4. Monthly Trend */}
      <TrendChart projectId={projectId} gridColor={gridColor} tickColor={tickColor} />

      {/* 5. Custom KPIs built with Cognara */}
      {(customWidgets.length > 0 || customLoading) && (
        <div className="p-4 rounded-lg border" data-testid="swm-custom-widgets"
             style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" style={{ color: '#F26B1F' }} />
              <h4 className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>Custom KPIs</h4>
              <span className="text-[10px] opacity-60">Built with Cognara</span>
            </div>
            <button onClick={() => setBuilderOpen(true)} data-testid="swm-add-custom-widget"
              className="flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md font-semibold transition-all"
              style={{ background: 'rgba(242,107,31,0.12)', color: '#F26B1F' }}>
              <Plus className="w-3 h-3" /> Add another
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {customWidgets.map((w) => (
              <div key={w.widget_id} className="rounded-lg border p-3 relative group"
                   style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}
                   data-testid={`swm-custom-widget-${w.widget_id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{w.spec?.title}</div>
                    <div className="text-[10px] opacity-60 truncate">
                      {w.spec?.chart_type} · {w.spec?.dataset} · {w.spec?.metric}
                      {w.spec?.group_by ? ` · by ${w.spec.group_by}` : ''}
                    </div>
                  </div>
                  <button onClick={() => deleteWidget(w.widget_id)}
                    data-testid={`swm-custom-widget-delete-${w.widget_id}`}
                    className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity w-6 h-6 rounded flex items-center justify-center text-red-500 hover:bg-red-500/10">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="h-52">
                  <WidgetChart spec={w.spec} data={w.preview} compact />
                </div>
              </div>
            ))}
            {customLoading && customWidgets.length === 0 && (
              <div className="col-span-full py-6 flex justify-center opacity-60">
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cognara chat drawer */}
      <KpiBuilderChat
        open={builderOpen}
        onClose={() => setBuilderOpen(false)}
        projectId={projectId}
        onSaved={(w) => setCustomWidgets((prev) => [...prev, w])}
      />

      {/* Legacy: Allocation donut + weight comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <h4 className="text-[11px] font-bold mb-3" style={{ color: 'var(--text-primary)' }}>Allocation State Distribution</h4>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={stateData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={2}
                     dataKey="value" nameKey="name"
                     label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                     labelLine={{ stroke: tickColor }} style={{ fontSize: 9 }}>
                  {stateData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Pie>
                <Tooltip contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <h4 className="text-[11px] font-bold mb-3" style={{ color: 'var(--text-primary)' }}>Weight Comparison (kg)</h4>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weightData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis dataKey="name" tick={{ fill: tickColor, fontSize: 10 }} />
                <YAxis tick={{ fill: tickColor, fontSize: 10 }} />
                <Tooltip contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', fontSize: 10 }} />
                <Bar dataKey="value" name="Weight (kg)" radius={[4, 4, 0, 0]}>
                  {weightData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
